import { normalizeImportDate } from "./importDates";
export const engagementFields = [
  ["engagementName", "Engagement Name", "name", true],
  ["workType", "Work Type", "workType", true],
  ["clientName", "Client Name", "client", true],
  ["clientCode", "Client Code", "clientCode"],
  ["clientType", "Client Type", "clientType"],
  ["industry", "Industry", "industry"],
  ["financialYearEnd", "Financial Year End"],
  ["description", "Description", "description"],
  ["dueDate", "Due Date", "dueDate"],
  ["partner", "Engagement Partner", "partner"],
  ["manager", "Manager", "manager"],
  ...[
    "New Client",
    "New Partner",
    "Complex Transaction",
    "Going Concern",
    "High Risk Industry",
    "Prior Year Adjustments",
    "Federal Funding",
    "Prior Inspection Findings",
    "Public Company",
    "Other Risks",
  ].map((label) => ["risk" + label.replace(/ /g, ""), "Risk " + label]),
];

const normalize = (value) =>
  String(value || "")
    .replace(/[^a-z0-9]/gi, "")
    .toLowerCase();
export const suggestExactMappings = (headers) =>
  Object.fromEntries(
    engagementFields.map(([key, label]) => {
      const matches = headers.filter((header) =>
        [normalize(key), normalize(label)].includes(normalize(header))
      );
      return [key, matches.length === 1 ? matches[0] : ""];
    })
  );
export function validateHeaderRow(matrix, headerRow) {
  if (!Number.isInteger(headerRow) || headerRow < 1 || headerRow > 100)
    return "Enter a whole row number between 1 and 100 for the column headings, not the number of engagements.";
  const headers = (matrix[headerRow - 1] || []).map((value) =>
    String(value ?? "").trim()
  );
  if (!headers.length || headers.every((header) => !header))
    return `Row ${headerRow} is empty or outside this worksheet. Select the row containing column headings.`;
  if (headers.length > 200)
    return `Row ${headerRow} contains more than 200 columns. Use a worksheet with up to 200 columns.`;
  if (headers.some((header) => !header))
    return `Row ${headerRow} has blank column headings. Choose the headings row or give every column a name in your file.`;
  if (headers.some((header) => header.length > 200))
    return `Row ${headerRow} has a heading longer than 200 characters. Shorten it in your file or select the correct headings row.`;
  if (new Set(headers.map((header) => header.toLowerCase())).size !== headers.length)
    return `Row ${headerRow} has duplicate column headings. Give each column a unique name in your file or select the correct headings row.`;
  if (!matrix.slice(headerRow).some((row) => row.some((value) => String(value ?? "").trim())))
    return `There are no engagement rows below row ${headerRow}. Select the headings above your engagement data.`;
  return "";
}
export function mappedSourceRows(matrix, headerRow, mappings) {
  const headers = (matrix[headerRow - 1] || []).map((value) =>
    String(value ?? "").trim()
  );
  return matrix
    .slice(headerRow)
    .map((values, index) => ({ values, index }))
    .filter(({ values }) =>
      values.some(
        (value) =>
          value !== null && value !== undefined && String(value).trim() !== ""
      )
    )
    .map(({ values, index }) =>
      Object.fromEntries([
        ["__sourceRow", headerRow + index + 1],
        ...engagementFields.map(([key]) => [
          key,
          mappings[key] ? values[headers.indexOf(mappings[key])] ?? "" : "",
        ]),
        [
          "Engagement Partner",
          mappings.partner
            ? values[headers.indexOf(mappings.partner)] ?? ""
            : "",
        ],
        [
          "Manager",
          mappings.manager
            ? values[headers.indexOf(mappings.manager)] ?? ""
            : "",
        ],
      ])
    );
}
export function canonicalEngagementRows(rows) {
  return [
    engagementFields.map(([, label]) => label),
    ...rows
      .filter((row) => row.included)
      .map((row) =>
        engagementFields.map(([key, , rowKey]) =>
          ["dueDate", "financialYearEnd"].includes(key)
            ? normalizeImportDate(rowKey ? row[rowKey] : row.source[key], { date1904: row.date1904, dateOrder: row.dateOrder }) || (rowKey ? row[rowKey] : row.source[key]) || ""
            : rowKey
            ? row[rowKey] === "—"
              ? ""
              : row[rowKey]
            : row.source[key] ?? ""
        )
      ),
  ];
}

// Row numbers are physical worksheet rows, including leading blank rows.
export function suggestHeaderRow(matrix) {
  const names = new Set(
    engagementFields.flatMap(([key, label]) => [
      normalize(key),
      normalize(label),
    ])
  );
  ["Job", "Customer", "Service", "Period End", "Sector", "Client Ref"].forEach(
    (name) => names.add(normalize(name))
  );
  let bestRow =
    matrix.findIndex((row) => row.some((value) => String(value ?? "").trim())) +
    1;
  let bestScore = 1;
  matrix.slice(0, 100).forEach((row, index) => {
    const values = row.map((value) =>
      typeof value === "string" ? value.trim() : ""
    );
    if (
      values.length < 2 ||
      values.some((value) => !value) ||
      new Set(values.map(normalize)).size !== values.length
    )
      return;
    const score = values.filter((value) => names.has(normalize(value))).length;
    if (score > bestScore) {
      bestScore = score;
      bestRow = index + 1;
    }
  });
  return bestRow || 1;
}
